<template lang="pug">
.c-question-detail-page
  app-main-content-layout(
    title="Question Answers"
    backTo="/manage-content/media/questions"
  )
    template(#actions)
      el-button(
        type="primary"
        @click="onExportCSV"
        :loading="isExporting"
      ).has-fai.min-w-110
        fai(type="fal" icon="file-export").size-18
        span Export CSV

    div(v-if="question && !apiError")
      .c-content-section.mb-3
        .question-header
          h3.question-title {{ question.content }}
          p.question-date Created: {{ formatDate(question.createdAt) }}

      .c-content-section
        .answers-section
          el-table(
            :data="answers.items"
            v-loading="loading"
            style="width: 100%"
          )
            el-table-column(width="40")

            el-table-column(prop="content" label="Answer" min-width="400")
              template(#default="scope")
                .table-title {{ scope.row.content }}

            el-table-column(prop="createdAt" label="Created Date" min-width="200")
              template(#default="scope")
                .table-date {{ formatDate(scope.row.createdAt) }}

          .pagination-container.mt-4(v-if="answers.items.length > 0")
            .simple-pagination.d-flex.align-items-center.justify-content-center
              .page-nav.d-flex
                el-button(
                  @click="handleCurrentChange(currentPage - 1)"
                  :disabled="currentPage === 1 || loading"
                  size="small"
                ) Prev
                .page-numbers.mx-2 {{ currentPage }}
                el-button(
                  @click="handleCurrentChange(currentPage + 1)"
                  :disabled="!hasMorePages || loading"
                  size="small"
                ) Next
              .page-info.ms-3(v-if="answers.items.length > 0")
                | Showing {{ startItem }}-{{ endItem }}

          .no-data.text-center.my-5(v-if="!loading && answers.items.length === 0")
            .no-data-icon.mb-3
              fai(type="fal" icon="comments").size-48
            h3 No answers found
            p This question doesn't have any answers yet.

    .c-content-section.api-error.text-center(v-if="apiError")
      .error-icon.mb-3
        fai(type="fal" icon="exclamation-triangle").size-48
      h3 Error loading question answers
      p {{ apiError }}
      el-button(
        type="primary"
        @click="getAnswers"
      ).mt-3 Retry
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Page Container
.c-question-detail-page {
  padding: 2rem;
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Question Header
.question-header {
  padding: 0;
}

.question-title {
  margin: 0 0 0.75rem 0;
  color: $gray-900;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
}

.question-date {
  margin: 0;
  color: $gray-500;
  font-size: 0.875rem;
  font-weight: 500;
}

// Answers Section
.answers-section {
  background: transparent;
}

// Table Styling
:deep(.el-table) {
  background: transparent;
  
  th {
    background-color: $gray-50;
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    border-bottom: 1px solid $gray-200;
  }

  td {
    border-bottom: 1px solid $gray-100;
    color: $gray-700;
    font-size: 0.875rem;
  }

  tr:hover > td {
    background-color: color.adjust($color-purple-text, $lightness: 47%);
  }

  .el-table__empty-block {
    background: transparent;
  }
}

.table-title {
  font-weight: 500;
  line-height: 1.5;
  color: $gray-900;
}

.table-date {
  color: $gray-500;
  font-weight: 500;
}

// Pagination
.pagination-container {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0 0.5rem;
}

.simple-pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-numbers {
  font-weight: 600;
  color: $gray-700;
  font-size: 0.875rem;
  min-width: 2rem;
  text-align: center;
}

.page-info {
  color: $gray-600;
  font-size: 0.875rem;
  font-weight: 500;
}

// Empty State
.no-data {
  padding: 3rem 1rem;
  color: $gray-600;

  .no-data-icon {
    color: $gray-400;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: $gray-900;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.875rem;
    color: $gray-500;
    margin: 0;
  }
}

// Error State
.api-error {
  padding: 3rem 1rem;

  .error-icon {
    color: #f56c6c;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #f56c6c;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.875rem;
    color: $gray-600;
    margin-bottom: 1rem;
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
}

// Icon Sizes
.size-18 {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.size-48 {
  font-size: 48px;
  width: 48px;
  height: 48px;
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Responsive Design
@media (max-width: 768px) {
  .c-question-detail-page {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  .question-title {
    font-size: 1.125rem;
  }

  .simple-pagination {
    flex-direction: column;
    gap: 0.75rem;
  }

  .page-info {
    margin-left: 0;
  }
}

@media (max-width: 576px) {
  .c-question-detail-page {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }

  .question-title {
    font-size: 1rem;
  }
}
</style>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, computed,
} from 'vue';
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import shared from '../shared';
import { findAllFeaturedQuestions, findAllFeaturedQuestionAnswers } from '@/graphs/gql';

export default defineComponent({
  name: 'ManageContent_Media_QuestionDetail',
  setup() {
    const { makeGraphRequest, getAuthToken, loading } = shared();
    const route = useRoute();
    const answers = reactive({ items: [] });
    const question = ref<any>(null);
    const apiError = ref<any>(null);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const hasMorePages = ref(true);
    const isExporting = ref(false);

    const questionId = computed(() => route.params.id);

    // Computed properties for pagination display
    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + answers.items.length;
    });

    async function getQuestion() {
      try {
        const response = await makeGraphRequest('query', findAllFeaturedQuestions, {
          take: 100,
          skip: 0,
        }, getAuthToken());

        // Find the specific question by ID
        const questions = response.data.findAllFeaturedQuestions;
        question.value = questions.find((q) => q.id === questionId.value);

        if (!question.value) {
          apiError.value = 'Question not found';
        }
      } catch (err) {
        console.error('Error fetching question:', err);
        apiError.value = err.message || 'Failed to fetch question';
      }
    }

    async function getAnswers() {
      try {
        loading.value = true;
        apiError.value = null;

        // Ensure we have a valid questionId before making the request
        if (!questionId.value) {
          apiError.value = 'Invalid question ID';
          return;
        }

        const skip = (currentPage.value - 1) * pageSize.value;
        const response = await makeGraphRequest('query', findAllFeaturedQuestionAnswers, {
          questionId: questionId.value, // This should now be a valid UUID
          take: pageSize.value,
          skip,
        }, getAuthToken());

        const fetchedAnswers = response.data.findAllFeaturedQuestionAnswers;
        // Handle edge case: if we got no results and we're not on page 1
        if (fetchedAnswers.length === 0 && currentPage.value > 1) {
          // Go back to the previous page
          currentPage.value -= 1;
          hasMorePages.value = false; // We know there are no more pages

          // Show user feedback
          ElMessage.info(`Reached end of results. Showing page ${currentPage.value}.`);

          // Recursively call getAnswers to fetch the previous page
          await getAnswers();
          return;
        }

        answers.items = fetchedAnswers;

        // Check if we received a full page of results
        // If we got less than pageSize items, there are no more pages
        hasMorePages.value = fetchedAnswers.length === pageSize.value;

        // Additional safety: if we got exactly 0 results on page 1, no more pages
        if (fetchedAnswers.length === 0 && currentPage.value === 1) {
          hasMorePages.value = false;
        }
      } catch (err) {
        console.error('Error fetching answers:', err);
        apiError.value = err.message || 'Failed to fetch answers';
      } finally {
        loading.value = false;
      }
    }

    function formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    function handleCurrentChange(page) {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }

      // Prevent going to the same page
      if (page === currentPage.value) {
        return;
      }

      currentPage.value = page;
      getAnswers();
    }

    async function getAllAnswers() {
      try {
        // Fetch all answers for export (not paginated)
        const response = await makeGraphRequest('query', findAllFeaturedQuestionAnswers, {
          questionId: questionId.value,
          take: 1000, // Large number to get all answers
          skip: 0,
        }, getAuthToken());

        return response.data.findAllFeaturedQuestionAnswers;
      } catch (err) {
        console.error('Error fetching all answers for export:', err);
        throw err;
      }
    }

    const onExportCSV = async () => {
      if (!question.value) {
        ElMessage.error('No question data available for export');
        return;
      }

      isExporting.value = true;

      try {
        // Get all answers for export
        const allAnswers = await getAllAnswers();

        // Create CSV content with question info at top, then answers
        const csvRows = [
          // Header row
          'Type,Content,Created Date',
          // Question row
          `"Question","${question.value.content}","${formatDate(question.value.createdAt)}"`,
          // Empty row for separation
          ',,',
        ];

        // Add answer rows
        if (allAnswers.length > 0) {
          allAnswers.forEach((answer) => {
            csvRows.push(`"Answer","${answer.content}","${formatDate(answer.createdAt)}"`);
          });
        } else {
          csvRows.push('"Answer","No answers available",""');
        }

        const csvContent = csvRows.join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const now = new Date();
        const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
        const fileName = `question_answers_${dateTimeString}.csv`;

        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(blob, fileName);
        } else {
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', fileName);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        ElMessage.success('CSV exported successfully');
      } catch (error) {
        console.error('Error exporting CSV:', error);
        ElMessage.error('Failed to export CSV');
      } finally {
        isExporting.value = false;
      }
    };

    onMounted(async () => {
      await getQuestion();
      await getAnswers();
    });

    return {
      answers,
      question,
      loading,
      apiError,
      currentPage,
      pageSize,
      hasMorePages,
      startItem,
      endItem,
      isExporting,
      getAnswers,
      formatDate,
      handleCurrentChange,
      onExportCSV,
    };
  },
});
</script>
